import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';

import { UploadStatusWidget, UploadStatuses, UploadStatus } from '../../utils/import';
import { AsyncTask, AsyncTasksTypes } from '../../utils/asyncTasks';
import { getAsyncTask } from '../../apiClient';

interface DryRunDialogProps {
    title: string;
    asyncTask?: AsyncTask;
    taskStateError: string;
    onCancel: () => void;
    onConfirm: (dryRunAsyncTaskId: string) => void;
    downloadFile: (id: string) => unknown;
}

interface TaskState {
    status: UploadStatus;
    task: AsyncTask | null;
    error: string | null;
}

const DryRunDialog: React.FC<DryRunDialogProps> = ({
    title,
    asyncTask,
    taskStateError,
    onCancel,
    onConfirm,
    downloadFile,
}) => {
    const [taskState, setTaskState] = useState<TaskState>({
        status: UploadStatuses.pending,
        task: null,
        error: null,
    });
    const [currentAsyncTask, setCurrentAsyncTask] = useState(null);
    const intervalIdRef = useRef<number | null>(null);
    const [hasClickedRun, setHasClickedRun] = useState(false);
    const runClickedRef = useRef(false);

    useEffect(() => {
        // Clear any existing interval
        if (intervalIdRef.current !== null) {
            clearInterval(intervalIdRef.current);
        }
        // Set new interval and store the ID in the ref
        intervalIdRef.current = window.setInterval(loadTaskState, 2000);
        return () => {
            if (intervalIdRef.current !== null) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };
    }, [asyncTask, taskStateError]);

    useEffect(() => {
        if (!!taskStateError) {
            setTaskState({
                task: null,
                error: taskStateError,
                status: UploadStatuses.finished,
            });
        }
    }, [taskStateError]);

    const loadTaskState = async () => {
        if (taskStateError) {
            setTaskState({
                status: UploadStatuses.finished,
                task: null,
                error: taskStateError,
            });
            // Clear interval when task has error
            if (intervalIdRef.current !== null) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        } else if (asyncTask) {
            try {
                const response = await getAsyncTask(asyncTask.type, asyncTask.id);
                const taskStatus =
                    response.task.finishedAt && response.task.children && response.task.children.length > 0
                        ? UploadStatuses.finished
                        : UploadStatuses.pending;

                setTaskState({
                    status: taskStatus,
                    task: response.task,
                    error: null,
                });
                setCurrentAsyncTask(response.task);

                // Clear interval when task status is finished
                if (taskStatus === UploadStatuses.finished && intervalIdRef.current !== null) {
                    clearInterval(intervalIdRef.current);
                    intervalIdRef.current = null;
                }
            } catch (error) {
                setTaskState((prevState) => ({
                    ...prevState,
                    status: UploadStatuses.finished,
                }));

                // Clear interval when there's an error
                if (intervalIdRef.current !== null) {
                    clearInterval(intervalIdRef.current);
                    intervalIdRef.current = null;
                }
            }
        }
    };

    const handleRunClick = () => {
        if (runClickedRef.current) {
            return;
        }
        runClickedRef.current = true;
        setHasClickedRun(true);
        if (taskState.task) {
            onConfirm(taskState.task.id);
        }
    };

    const shouldRunButtonBeDisabled = () => {
        if (taskState.status !== UploadStatuses.finished) {
            return true;
        }
        if (!taskState.task) {
            return true;
        } else if (
            (taskState.task.additionalInfo?.failsCount as number) > 0 ||
            (currentAsyncTask &&
                currentAsyncTask.type !== AsyncTasksTypes.MOBILE_DEVICES_IMPORT &&
                taskState.task.dryRunErrors &&
                taskState.task.dryRunErrors.length > 0)
        ) {
            return true;
        } else if (currentAsyncTask && currentAsyncTask.errors && currentAsyncTask.errors.length > 0) {
            return true;
        }
        return false;
    };

    return (
        <Modal backdrop="static" centered keyboard={false} onHide={onCancel} show>
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <UploadStatusWidget
                    downloadFile={(type, id) => downloadFile(id)}
                    importError={taskStateError}
                    importStatus={taskState.status}
                    importTask={taskState.task}
                />
            </Modal.Body>
            <Modal.Footer>
                {!!(taskState.task?.errors?.length || taskState.task?.warnings?.length) && (
                    <p className="modal-label">The link above provided the validation results.</p>
                )}
                <button className="wfp-btn" onClick={onCancel} style={{ marginRight: 10 }} type="button">
                    Cancel
                </button>
                <button
                    className="wfp-btn--primary"
                    disabled={shouldRunButtonBeDisabled() || hasClickedRun}
                    onClick={handleRunClick}
                    type="button"
                >
                    Run import
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default DryRunDialog;
